import {
  isUUID,
  refineFiltersAndOptions,
  replaceLocaleInPath,
  shortenMasterDataIds,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type Language,
  type MarketCountryCode,
} from '@ev-tech/search-modules-api';

import { replaceDeprecatedParams, simplifyPropertyTypeSubType } from './validation';
import { stringifySearchParams } from './searchParams';
import { type AdditionalUiStateParams } from '../types/types';
import { INTERNAL_PREFERENCE_PREFIX } from '../consts/aiSearch';

const DEFAULT_PRESERVED_PARAMS = ['gclid', /^gad_/, /^utm_/, 'msclkid', 'fbclid', 'ga4id', 'gbraid', 'wbraid'] as const;

const PRESERVED_PARAMS_FOR_UNSUBSCRIBE_SEARCH_ALERT = ['deleteAlert', 'authKey'] as const;

const PRESERVED_PARAMS_FOR_CLERK = [/^__clerk/] as const;

export type GetSearchResultPageUrlOptions = Partial<SearchListingsOptions> & {
  countryCode: MarketCountryCode;
  language: Language;
};

export type GetSearchResultPageUrlProps = {
  url: string | URL;
  filters: Partial<SearchListingsFilters>;
  options: GetSearchResultPageUrlOptions;
  preservedParams?: (string | RegExp)[];
  additionalParams?: Record<string, unknown> & AdditionalUiStateParams;
};

export const prepareFiltersForUrl = (
  filters: Partial<SearchListingsFilters>,
  options: GetSearchResultPageUrlOptions,
): SearchListingsFilters => {
  const replacedFilters: Partial<SearchListingsFilters> = replaceDeprecatedParams(filters);
  // remove price and/or surface filters when currency and/or measurementSystem are not provided
  const { filters: refinedFilters } = refineFiltersAndOptions(replacedFilters, options);
  // avoid adding fallbackSearch to the URL
  delete refinedFilters.fallbackSearch;
  // avoid adding privateOffice to the URL
  delete refinedFilters.privateOffice;

  delete refinedFilters.statuses;

  delete refinedFilters.since;

  // hide smart sort preferences for url
  if (refinedFilters.preferences) {
    refinedFilters.preferences = Object.fromEntries(
      Object.entries(refinedFilters.preferences).filter(([key]) => !key.startsWith(INTERNAL_PREFERENCE_PREFIX)),
    );
  }

  if (refinedFilters.shopIds?.some((id: string) => isUUID(id)))
    console.warn('masterDataShopId was passed as shopId. Please use the masterDataShopIds filter instead.');

  if (refinedFilters.masterDataShopIds) {
    refinedFilters.masterDataShopIds = shortenMasterDataIds(refinedFilters.masterDataShopIds);
  }

  if (refinedFilters.propertyTypeSubType) {
    refinedFilters.propertyTypeSubType = simplifyPropertyTypeSubType(refinedFilters.propertyTypeSubType);
  }

  return refinedFilters;
};

export const removeNonPreservedParams = (url: URL, preservedParams: (string | RegExp)[]): URL => {
  // loop through the current search params and remove any that are not in the preservedParams array
  const keys = [...url.searchParams.keys()];
  for (const key of keys) {
    const isPreservedParam = [
      ...DEFAULT_PRESERVED_PARAMS,
      ...PRESERVED_PARAMS_FOR_UNSUBSCRIBE_SEARCH_ALERT,
      ...PRESERVED_PARAMS_FOR_CLERK,
      ...preservedParams,
    ].some((param) => (typeof param === 'string' ? key === param : param.test(key)));
    if (!isPreservedParam) {
      url.searchParams.delete(key);
    }
  }
  return url;
};

/**
 * Construct a URL for the property search results page based on given filters and options.
 *
 * @deprecated Use {@link getSearchResultPageLink} instead for a unified approach that handles both URLs and paths.
 *
 * @function
 * @param {Object} params - The parameters object containing all configuration for generating the search URL.
 * @param {string | URL} params.url - The base URL to which search parameters will be appended.
 * @param {Partial<SearchListingsFilters>} params.filters - Filters to apply to the property search. These include things like place information, price range, and property type.
 * @param {GetSearchResultPageUrlOptions} params.options - Search options: language, countryCode etc.
 * @param {(string | RegExp)[]} [params.preservedParams] - Optional array of parameters in the input url that are preserved. (e.g. gclid, utm_*)
 * @param {Record<string, unknown>} [params.additionalParams] - Optional additional parameters to include in the search URL.
 * @returns {URL} A URL representing the full URL to the search results page.
 *
 * @summary Construct a URL for the property search results page based on given filters and options.
 */

export function getSearchResultPageUrl(params: GetSearchResultPageUrlProps): URL {
  const { url, filters, options, preservedParams = [], additionalParams = {} } = params;
  const { language, countryCode, pageSize, ...otherOptions } = options;
  let _url = url instanceof URL ? url : new URL(url);

  const preparedFilters = prepareFiltersForUrl(filters, options);

  _url = removeNonPreservedParams(_url, preservedParams);
  _url.pathname = replaceLocaleInPath(_url.pathname, language, countryCode);

  const stringifiedParams = stringifySearchParams({ ...preparedFilters, ...otherOptions, ...additionalParams }, _url.searchParams);
  _url.search = stringifiedParams;
  return _url;
}
